import React from 'react';
import { Button } from '@/components/Button';

interface CountryForm {
  countryCode: string;
  countryNameEN: string;
  countryNameVI: string;
}

interface CategoryForm {
  categoryCode: string;
  categoryNameEN: string;
  categoryNameVI: string;
}

interface TopicForm {
  topicCode: string;
  topicNameEN: string;
  topicNameVI: string;
}

interface TagForm {
  tag: string;
}

interface ArticleConfigModalsProps {
  idPrefix: string;
  isLoading: boolean;
  showAddCountryModal: boolean;
  showAddCategoryModal: boolean;
  showAddTopicModal: boolean;
  showAddTagModal: boolean;
  addCountryForm: CountryForm;
  addCategoryForm: CategoryForm;
  addTopicForm: TopicForm;
  addTagForm: TagForm;
  setAddCountryForm: React.Dispatch<React.SetStateAction<CountryForm>>;
  setAddCategoryForm: React.Dispatch<React.SetStateAction<CategoryForm>>;
  setAddTopicForm: React.Dispatch<React.SetStateAction<TopicForm>>;
  setAddTagForm: React.Dispatch<React.SetStateAction<TagForm>>;
  onCloseAddCountryModal: () => void;
  onCloseAddCategoryModal: () => void;
  onCloseAddTopicModal: () => void;
  onCloseAddTagModal: () => void;
  onSubmitAddCountry: () => void;
  onSubmitAddCategory: () => void;
  onSubmitAddTopic: () => void;
  onSubmitAddTag: () => void;
}

export const ArticleConfigModals: React.FC<ArticleConfigModalsProps> = ({
  idPrefix,
  isLoading,
  showAddCountryModal,
  showAddCategoryModal,
  showAddTopicModal,
  showAddTagModal,
  addCountryForm,
  addCategoryForm,
  addTopicForm,
  addTagForm,
  setAddCountryForm,
  setAddCategoryForm,
  setAddTopicForm,
  setAddTagForm,
  onCloseAddCountryModal,
  onCloseAddCategoryModal,
  onCloseAddTopicModal,
  onCloseAddTagModal,
  onSubmitAddCountry,
  onSubmitAddCategory,
  onSubmitAddTopic,
  onSubmitAddTag,
}) => {
  return (
    <>
      {showAddCountryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Add New Country</h2>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor={`${idPrefix}-countryCode`}
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Country Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id={`${idPrefix}-countryCode`}
                  value={addCountryForm.countryCode}
                  onChange={e =>
                    setAddCountryForm(prev => ({ ...prev, countryCode: e.target.value }))
                  }
                  placeholder="e.g., AU, UK, US"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label
                  htmlFor={`${idPrefix}-countryNameEN`}
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Country Name (EN) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id={`${idPrefix}-countryNameEN`}
                  value={addCountryForm.countryNameEN}
                  onChange={e =>
                    setAddCountryForm(prev => ({ ...prev, countryNameEN: e.target.value }))
                  }
                  placeholder="e.g., Australia"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label
                  htmlFor={`${idPrefix}-countryNameVI`}
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Country Name (VI) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id={`${idPrefix}-countryNameVI`}
                  value={addCountryForm.countryNameVI}
                  onChange={e =>
                    setAddCountryForm(prev => ({ ...prev, countryNameVI: e.target.value }))
                  }
                  placeholder="e.g., Uc"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={onCloseAddCountryModal}
                disabled={isLoading}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={onSubmitAddCountry}
                loading={isLoading}
                disabled={isLoading}
                className="flex-1"
              >
                Add Country
              </Button>
            </div>
          </div>
        </div>
      )}

      {showAddCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Add New Category</h2>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor={`${idPrefix}-categoryCode`}
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Category Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id={`${idPrefix}-categoryCode`}
                  value={addCategoryForm.categoryCode}
                  onChange={e =>
                    setAddCategoryForm(prev => ({ ...prev, categoryCode: e.target.value }))
                  }
                  placeholder="e.g., tech, sports"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label
                  htmlFor={`${idPrefix}-categoryNameEN`}
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Category Name (EN) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id={`${idPrefix}-categoryNameEN`}
                  value={addCategoryForm.categoryNameEN}
                  onChange={e =>
                    setAddCategoryForm(prev => ({ ...prev, categoryNameEN: e.target.value }))
                  }
                  placeholder="e.g., Technology"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label
                  htmlFor={`${idPrefix}-categoryNameVI`}
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Category Name (VI) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id={`${idPrefix}-categoryNameVI`}
                  value={addCategoryForm.categoryNameVI}
                  onChange={e =>
                    setAddCategoryForm(prev => ({ ...prev, categoryNameVI: e.target.value }))
                  }
                  placeholder="e.g., Cong nghe"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={onCloseAddCategoryModal}
                disabled={isLoading}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={onSubmitAddCategory}
                loading={isLoading}
                disabled={isLoading}
                className="flex-1"
              >
                Add Category
              </Button>
            </div>
          </div>
        </div>
      )}

      {showAddTopicModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Add New Topic</h2>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor={`${idPrefix}-topicCode`}
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Topic Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id={`${idPrefix}-topicCode`}
                  value={addTopicForm.topicCode}
                  onChange={e => setAddTopicForm(prev => ({ ...prev, topicCode: e.target.value }))}
                  placeholder="e.g., education, health"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label
                  htmlFor={`${idPrefix}-topicNameEN`}
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Topic Name (EN) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id={`${idPrefix}-topicNameEN`}
                  value={addTopicForm.topicNameEN}
                  onChange={e =>
                    setAddTopicForm(prev => ({ ...prev, topicNameEN: e.target.value }))
                  }
                  placeholder="e.g., Education"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label
                  htmlFor={`${idPrefix}-topicNameVI`}
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Topic Name (VI) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id={`${idPrefix}-topicNameVI`}
                  value={addTopicForm.topicNameVI}
                  onChange={e =>
                    setAddTopicForm(prev => ({ ...prev, topicNameVI: e.target.value }))
                  }
                  placeholder="e.g., Giao duc"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={onCloseAddTopicModal}
                disabled={isLoading}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={onSubmitAddTopic}
                loading={isLoading}
                disabled={isLoading}
                className="flex-1"
              >
                Add Topic
              </Button>
            </div>
          </div>
        </div>
      )}

      {showAddTagModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Add New Tag</h2>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor={`${idPrefix}-tag`}
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Tag <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id={`${idPrefix}-tag`}
                  value={addTagForm.tag}
                  onChange={e => setAddTagForm(prev => ({ ...prev, tag: e.target.value }))}
                  placeholder="e.g., education, politics"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={onCloseAddTagModal}
                disabled={isLoading}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={onSubmitAddTag}
                loading={isLoading}
                disabled={isLoading}
                className="flex-1"
              >
                Add Tag
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
